import React from 'react';
import styles from '../irlObservations2026.module.css';
import { DetailHeader, DetailTabs, FileRow, IrlCounts, ResponseComposer } from './shared';

function versionLabel(version) {
    if (!version) return 'Response';
    return String(version).toLowerCase().startsWith('v') ? `Response ${version}` : `Response v${version}`;
}

export default function IRL_Response({
    request,
    tab,
    onTab,
    onBack,
    onOpenAi,
    onCopy,
    copied,
    onEdit,
    onCancelEdit,
    editingResponse,
    tabs,
    extraActions,
    onUpdateFields,
    responseDraft,
    onResponseDraft,
    onSubmitResponse,
    onSaveDraft,
    onOpenFile,
    busy,
}) {
    const response = request.response;
    const history = response?.history || [];
    const [openVersion, setOpenVersion] = React.useState(null);

    React.useEffect(() => {
        setOpenVersion(null);
    }, [request?.id]);

    function toggleHistory(version) {
        setOpenVersion(current => (current === version ? null : version));
    }

    return (
        <>
            <DetailHeader
                kind="irl"
                item={request}
                onBack={onBack}
                onOpenAi={onOpenAi}
                onCopy={onCopy}
                copied={copied}
                onEdit={onEdit}
                extraActions={extraActions}
                onUpdateFields={onUpdateFields}
                busy={busy}
            />
            <DetailTabs tabs={tabs} tab={tab} onTab={onTab} counts={IrlCounts(request)} />
            <div className={styles.detailBody}>
                <div className={styles.wideInner}>
                    {request.evidenceAlert && (
                        <div className={styles.alert}>
                            <p className={styles.alertTitle}>{request.evidenceAlert.title}</p>
                            <p className={styles.alertBody}>{request.evidenceAlert.body}</p>
                            <div className={styles.alertActions}>
                                <button type="button" className={styles.alertLink} onClick={() => onTab('Evidence')}>
                                    Review evidence
                                </button>
                                <button type="button" className={styles.alertLink} onClick={onOpenAi}>
                                    Analyse update →
                                </button>
                            </div>
                        </div>
                    )}
                    {response && (
                        <>
                            <div className={styles.responseHead}>
                                <span className={styles.responseTitle}>
                                    {versionLabel(response.version)}
                                    {response.current ? ' · Current' : ''}
                                </span>
                                <div className={styles.responseLinks}>
                                    {onEdit && (
                                        <button type="button" className={styles.textLink} onClick={onEdit}>
                                            Edit
                                        </button>
                                    )}
                                    {onCopy && (
                                        <button type="button" className={styles.textLink} onClick={onCopy}>
                                            {copied ? 'Copied' : 'Copy link'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.card}>
                                <p className={styles.responseBody}>{response.body}</p>
                                {response.evidence?.length > 0 && (
                                    <>
                                        <div className={styles.sectionLabelSm}>Evidence</div>
                                        {response.evidence.map(file => (
                                            <FileRow key={file.documentId || file.name} file={file} onOpen={onOpenFile} />
                                        ))}
                                    </>
                                )}
                                <div className={styles.responseSubmitted}>
                                    <span>
                                        Submitted by <span className={styles.responseSubmittedName}>{response.submittedBy}</span>
                                    </span>
                                    <span>{response.submittedOn}</span>
                                </div>
                                {response.readiness?.message && <p className={styles.metaPlain}>{response.readiness.message}</p>}
                            </div>
                        </>
                    )}
                    {history.length > 0 && (
                        <div className={styles.responseHistory}>
                            <div className={styles.sectionLabelSm}>Response history</div>
                            <div className={styles.responseHistoryList}>
                                {history.map(item => {
                                    const open = openVersion === item.version;
                                    return (
                                        <button
                                            key={item.version}
                                            type="button"
                                            className={styles.responseHistoryCard}
                                            aria-expanded={open}
                                            aria-label={`${open ? 'Hide' : 'Show'} ${versionLabel(item.version)}`}
                                            onClick={() => toggleHistory(item.version)}>
                                            <span className={styles.responseHistoryTitle}>
                                                <span>{versionLabel(item.version)}</span>
                                                {item.label === 'Current' && (
                                                    <span className={`${styles.badge} ${styles.badgeGreen}`}>{item.label}</span>
                                                )}
                                            </span>
                                            <span className={styles.historyDetail}>
                                                {item.date} · {item.by}
                                            </span>
                                            {open ? (
                                                <span className={styles.responseBody}>{item.response_text || ''}</span>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {editingResponse && onSubmitResponse && (
                        <ResponseComposer
                            value={responseDraft || ''}
                            onChange={onResponseDraft}
                            onSubmit={onSubmitResponse}
                            onSaveDraft={onSaveDraft}
                            onCancel={onCancelEdit}
                            busy={busy}
                        />
                    )}
                </div>
            </div>
        </>
    );
}
